import { Directive, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConnectedPosition, ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { first, isEqual } from 'lodash-es';
import { NimbusFilterSummaryButtonComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-summary-button/nimbus-filter-summary-button.component';
import { NimbusFilterSummaryInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-summary-input/nimbus-filter-summary-input.component';

@Directive()
export abstract class FilterBase<TFilterValue, TFilter> implements OnInit, OnChanges {
  @Input()
  public filterValue: TFilterValue;
  @Input()
  public filter: TFilter;
  @Input()
  public disabled: boolean = false;
  @Input()
  public openOnInit: boolean;

  @ViewChild(NimbusFilterSummaryButtonComponent)
  public summaryButton: NimbusFilterSummaryButtonComponent;
  @ViewChild(NimbusFilterSummaryInputComponent)
  public summaryInput: NimbusFilterSummaryInputComponent;

  @Output() public filterValueChanged = new EventEmitter<TFilterValue>();

  public summary: string;
  public isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Keep the overlay anchored where it opened: don't reposition it while the page/grid scrolls.
  public readonly scrollStrategy: ScrollStrategy = inject(ScrollStrategyOptions).reposition();

  // Prefer right-edge-aligned (end-end) so the overlay extends leftward from the trigger and stays
  // anchored to the trigger's right edge — matches the services grid behaviour that bug #145932
  // calls out as the expected position. Fall back to start-start when there isn't enough room on
  // the left, then try the above-trigger variants when there's no room below.
  public readonly cdkConnectedPositions: ConnectedPosition[] = [
    { panelClass: 'right-top', originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 10 },
    { panelClass: 'right-top', originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 10 },
    { panelClass: 'right-top', originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -10 },
    { panelClass: 'right-top', originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -10 },
  ];

  protected abstract valuesEmpty: TFilterValue[];
  protected abstract getSummary(): string;
  protected abstract onFilterValueChanged(): void;

  public ngOnInit(): void {
    this.isOpen$.next(this.openOnInit);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterValue || changes.filter) {
      this.onFilterValueChanged();
      this.summary = this.getSummary();
    }
  }

  public get isValueEmpty(): boolean {
    return (this.valuesEmpty || []).some((x) => isEqual(this.filterValue, x));
  }

  public onSummaryClick(): void {
    this.isOpen$.next(!this.isOpen$.value);
  }

  public onOutsideClick(ev: MouseEvent): void {
    if (
      !ev.composedPath().includes(this.summaryButton?.el.nativeElement) &&
      !ev.composedPath().includes(this.summaryInput?.el.nativeElement)
    ) {
      this.isOpen$.next(false);
    }
  }

  public onRemoveFilter(): void {
    this.filterValueChanged.emit(first(this.valuesEmpty) || null);
  }
}
